/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/no-confusing-void-expression */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { yupResolver } from '@hookform/resolvers/yup'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Stack, Typography } from '@mui/material'
import { MButton, TextInput } from 'components'
import { color } from 'config/color'
import { USER_TOKEN } from 'config/constants'
import { CLIENT, MESSAGES } from 'config/projectConstants'
import { useAuth } from 'context/auth'
import { useSnackbar } from 'notistack'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { APIs } from 'services'
import { useLocalStorage } from 'utils/hooks'
import * as yup from 'yup'

export enum LinkAccountMode {
  LINK = 'LINK',
  UNLINK = 'UNLINK'
}

interface ILinkAccount {
  open: boolean
  mode: LinkAccountMode.LINK | LinkAccountMode.UNLINK
  handleClose: () => void
  accountId: string
  onSuccess?: () => void
}

interface VerifyAccountType {
  accountNumber: string
  serviceAddress: string
}


const verifySchema = yup.object({
  accountNumber: yup
    .string()
    .required('Account number is required')
    .test('not-primary-account', 'Cannot link the primary account number', function (value) {
      const { primaryAccountId } = this.options.context ?? {}
      return value !== primaryAccountId
    }),
  serviceAddress: yup.string().required('Service address is required')
})

const LinkAccount: React.FC<ILinkAccount> = ({ open, handleClose, mode, accountId: unlinkAccountId, onSuccess }) => {
  const { account } = useAuth()
  const [step, setStep] = useState<number>(1)
  const { enqueueSnackbar } = useSnackbar()
  const [authToken] = useLocalStorage(USER_TOKEN, '')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [formData, setFormData] = useState<VerifyAccountType | null>(null)
  const isWEBclient = process.env.REACT_APP_CLIENT_NAME === CLIENT.WEB
  const isCarmelClient = process.env.REACT_APP_CLIENT_NAME === CLIENT.CARMEL

  const {
    handleSubmit: handleVerifySubmit,
    control: verifyControl,
    formState: { errors: verifyErrors },
    reset: resetVerifyForm
  } = useForm<VerifyAccountType>({
    mode: 'all',
    defaultValues: {
      accountNumber: '',
      serviceAddress: ''
    },
    resolver: yupResolver(verifySchema),
    context: { primaryAccountId: account?.accountId }
  })

  const verifyAccount = async (data: VerifyAccountType) => {
    try {
      setIsLoading(true)
      const payload = {
        accountId: data.accountNumber,
        serviceAddress: data.serviceAddress.toUpperCase(),
        AccessToken: authToken
      }
      const response = isCarmelClient ? await APIs.verifyAccountToLink(payload) : await APIs.verifyAccount(payload)
      if (response.status !== 1) {
        throw Error(response.message.message)
      } else {
        enqueueSnackbar('Account verified successfully', { variant: 'success', autoHideDuration: 3000 })
        setFormData(data)
        setStep(2)
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message?.message || MESSAGES.FAILED_VERIFYING_ACCOUNT
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 3000 })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (mode === LinkAccountMode.UNLINK) {
      setStep(2)
    } else {
      setStep(1)
    }
  }, [mode, open])

  const unlinkAccountInCCS = async () => {
    return await APIs.updateInfo({
      accountId: account?.accountId ?? '',
      AccessToken: authToken,
      email: account?.ccsEmail,
      admin: true,
      addRemoveAcctUpdates: [
        {
          accountPropertyManager: account?.accountId ?? '',
          accountPropertyOwner: formData?.accountNumber ?? '',
          addRemoveAcctFlag: 'R'
        }
      ]
    }, authToken)
  }

  const handleUpdateAccount = async () => {
    try {
      setIsLoading(true)

      // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
      const updateResponse: any = await updateUserAccount()
      if (updateResponse?.status !== 1) {
        throw new Error(updateResponse.message.message || 'Failed updating account')
      }

      if (isWEBclient) {
        enqueueSnackbar('Account linked successfully.', { variant: 'success' })
        await resetFormState()
        return
      }

      const linkResponse = await linkUserAccount()
      const icErrorMessage = linkResponse?.data?.data?.Data?.AccountsNotLinked[0]?.Message
      const successMessage = linkResponse?.data?.data?.Message === 'Success'
      if (icErrorMessage) {
        const m = icErrorMessage.includes('not be linked') ? `${icErrorMessage} Please check InvoiceCloud.` : icErrorMessage
        // unlink account in CCS if account is not linked in InvoiceCloud
        await unlinkAccountInCCS()
        throw new Error(m)
      } else if (successMessage) {
        if (isCarmelClient) {
          const updateEmailResponse = await APIs.updateEmailAfterLink({
            accountId: formData?.accountNumber ?? '',
            accessToken: authToken
          })
          enqueueSnackbar(updateEmailResponse.message || 'Email updated successfully.', { variant: 'success', autoHideDuration: 5000 })
        }
        enqueueSnackbar('Account linked successfully.', { variant: 'success', autoHideDuration: 3000 })
      }

      await resetFormState()
    } catch (error: any) {
      if (error?.response?.data?.error?.includes('could not be linked')) {
        enqueueSnackbar('Account could not be linked. Please check InvoiceCloud.', { variant: 'error', autoHideDuration: 5000 })
        await unlinkAccountInCCS()
      } else if (error?.response?.data?.error) {
        enqueueSnackbar(error?.response?.data?.error, { variant: 'error', autoHideDuration: 5000 })
      } else enqueueSnackbar(error?.message || error.toString(), { variant: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  const updateUserAccount = async () => {
    return await APIs.updateInfo({
      accountId: account?.accountId ?? '',
      AccessToken: authToken,
      email: account?.ccsEmail,
      admin: true,
      addRemoveAcctUpdates: [
        {
          accountPropertyManager: account?.accountId ?? '',
          accountPropertyOwner: {
            [LinkAccountMode.LINK]: formData?.accountNumber,
            [LinkAccountMode.UNLINK]: unlinkAccountId
          }[mode] ?? '',
          addRemoveAcctFlag: mode === LinkAccountMode.UNLINK ? 'R' : 'A'
        }
      ]
    }, authToken)
  }

  const linkUserAccount = async () => {
    return await APIs.icLinkAccount({
      AccessToken: authToken,
      accountList: [formData?.accountNumber ?? ''],
      email: account?.ccsEmail,
      isAdmin: true,
      accountId: account?.accountId
    })
  }

  const resetFormState = async () => {
    // Then reset form and close
    setStep(0)
    resetVerifyForm()
    setFormData(null)
    handleClose()

    // Trigger onSuccess callback to refresh linked accounts
    if (onSuccess) {
      onSuccess()
    }
  }
  return (
    <Dialog fullWidth open={open} closeAfterTransition>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography fontSize={18} fontWeight={500}>
          {step === 1 ? 'Verify Account' : 'Update Account'}
        </Typography>
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={(theme) => ({

            color: theme.palette.grey[500]
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <DialogContentText sx={{ mb: 2 }}>
          {step === 1
            ? 'Enter the account number and service address to verify the account you want to link.'
            : `Confirm if you want to ${mode.toLowerCase()} this account.`}
        </DialogContentText>
        {step === 1
          ? (
            <>
              <TextInput
                fieldLabel='Account Number'
                fieldName='accountNumber'
                fieldType='text'
                placeholder='Enter your account number'
                control={verifyControl}
                errors={verifyErrors}
              />
              <TextInput
                fieldLabel='Service Address'
                fieldName='serviceAddress'
                fieldType='text'
                placeholder='Enter your service address'
                control={verifyControl}
                errors={verifyErrors}
              />

            </>
          )
          : mode === LinkAccountMode.LINK && <Stack direction="column" spacing={2} mb={2}>
            <Box >
              <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Account Number</Typography>
              <Typography textTransform="capitalize" fontSize={16}>{formData?.accountNumber}</Typography>
            </Box>
            <Box>
              <Typography letterSpacing={1} fontSize={12} color={color.grey[700]} fontWeight={400} textTransform="uppercase">Service Address</Typography>
              <Typography textTransform="capitalize" fontSize={16}>{formData?.serviceAddress}</Typography>
            </Box>
          </Stack>}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', mb: 5 }}>
        {step === 1
          ? <>
            <MButton
              type='button'
              variant='outlined'
              size='large'
              rounded='true'
              sx={{ width: '200px' }}
              texttransform='none'
              onClick={() => { resetVerifyForm() }}
            >
              Clear
            </MButton>
            <MButton
              type='button'
              variant='contained'
              size='large'
              rounded='true'
              sx={{ width: '200px' }}
              texttransform='none'
              onClick={handleVerifySubmit(verifyAccount)}
              loading={isLoading}
              disabled={isLoading}
            >
              Verify
            </MButton>
          </>
          : <>
            <MButton
              type='button'
              variant='outlined'
              size='large'
              rounded='true'
              sx={{ width: '200px' }}
              texttransform='none'
              onClick={() => { setStep(1) }}
            >
              Back
            </MButton>
            <MButton
              type='button'
              variant='contained'
              size='large'
              rounded='true'
              sx={{ width: '200px' }}
              texttransform='none'
              onClick={() => { void handleUpdateAccount() }}
              loading={isLoading}
              disabled={isLoading}
            >
              Update
            </MButton>
          </>}
      </DialogActions>
    </Dialog>
  )
}

export default LinkAccount
